/**
 * Guest/authed receipt panel on checkout complete (plan Screen AC Receipt).
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import { ANALYTICS_UNIVERSAL_EVENTS } from 'pi-kiosk-shared';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import { buildScreenMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import type { PaymentStatusDTO } from './checkoutApi.js';
import { useQueryClient } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useAccountPayments } from '../account/accountApi.js';
import { AccountReceiptDownloadButton } from '../account/components/AccountReceiptDownloadButton.js';
import { ReceiptLoginUpsellCard } from './ReceiptLoginUpsellCard.js';
import { SignedInAccountPromptCard } from '../auth/components/SignedInAccountPromptCard.js';
import { canMountEmbeddedSignInForm } from '../auth/routing/canMountEmbeddedSignInForm.js';
import type { PaymentHistoryItemDTO } from '../account/accountTypes.js';
import { resolvePromoEventDisplayName } from '../promotions/resolvePromoEventDisplayName.js';
import { readPromoReceiptSnapshot } from './checkoutPromoReceiptSnapshot.js';
import { navigateReceiptLoginAfterPaymentPark } from './checkoutReceiptLoginCta.js';
import {
  isSignedInNavChrome} from '../hub/customerHubPaths.js';
import { GuestReceiptRecoveryBlock } from './GuestReceiptRecoveryBlock.js';

interface CheckoutReceiptPanelProps {
  readonly paymentStatus: PaymentStatusDTO;
  readonly returnPathWithQuery: string;
  /** Analytics attribution; checkout complete keeps default, account embeds pass account_orders. */
  readonly screenName?: string;
  readonly salesPointId?: number | null;
}

function findReceiptRow(
  items: readonly PaymentHistoryItemDTO[],
  paymentId: string
): PaymentHistoryItemDTO | null {
  return items.find((item) => item.paymentId === paymentId) ?? null;
}

export function CheckoutReceiptPanel({
  paymentStatus,
  returnPathWithQuery,
  screenName = 'checkout_complete_receipt',
  salesPointId: _salesPointId = null,
}: CheckoutReceiptPanelProps): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const navigate = useNavigate();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const signedInReceipt = isSignedInNavChrome({ session });
  const canMountSignIn = canMountEmbeddedSignInForm({ session });
  const queryClient = useQueryClient();
  const useInlineReceiptLogin = !signedInReceipt && canMountSignIn;
  const analytics = useCustomerAnalytics();
  const { formatDateTime, formatPrice } = useCustomerFormatters();

  const paymentsQuery = useAccountPayments({ limit: 20 }, signedInReceipt);

  const receiptRow = useMemo(() => {
    if (paymentsQuery.data === undefined) {
      return null;
    }
    return findReceiptRow(paymentsQuery.data.items, paymentStatus.paymentId);
  }, [paymentStatus.paymentId, paymentsQuery.data]);

  useEffect(() => {
    if (!signedInReceipt) {
      analytics.track({
        eventName: 'receipt_viewed_guest' as AnalyticsEventName,
        screenName,
        metadata: {
          ...buildScreenMetadata({ screenName }),
          receipt_format: 'summary'}});
    }
    analytics.track({
      eventName: ANALYTICS_UNIVERSAL_EVENTS.RECEIPT_OPENED,
      screenName,
      metadata: {
        ...buildScreenMetadata({ screenName }),
        receipt_format: 'summary',
        viewer: signedInReceipt ? 'authenticated' : 'guest'}});
  }, [analytics, screenName, signedInReceipt]);

  const handleReceiptAuthenticated = useCallback((): void => {
    // G17 — scoped locales only; never broad accountPrefix (refetches /me).
    if (customerId === undefined) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
    });
  }, [customerId, queryClient, tenantCode]);

  const trackReceiptLoginCta = useCallback((): void => {
    analytics.track({
      eventName: 'receipt_login_cta_clicked' as AnalyticsEventName,
      screenName,
      metadata: buildScreenMetadata({ screenName })});
    analytics.track({
      eventName: ANALYTICS_UNIVERSAL_EVENTS.CTA_CLICKED,
      screenName,
      metadata: {
        ...buildScreenMetadata({ screenName }),
        cta_id: 'receipt_login'}});
  }, [analytics, screenName]);

  const handleLoginCta = useCallback(() => {
    trackReceiptLoginCta();
    navigateReceiptLoginAfterPaymentPark(returnPathWithQuery, tenantCode, navigate);
  }, [navigate, returnPathWithQuery, tenantCode, trackReceiptLoginCta]);

  const amountMinor = paymentStatus.amountMinor ?? receiptRow?.amountMinor;
  const currency = paymentStatus.currency ?? receiptRow?.currency;
  const completedAt = paymentStatus.completedAt ?? receiptRow?.completedAt;
  const promoReceiptSnapshot = useMemo(
    () => readPromoReceiptSnapshot(tenantCode, paymentStatus.paymentId),
    [paymentStatus.paymentId, tenantCode],
  );

  function renderDownloadSection(): JSX.Element {
    if (!signedInReceipt) {
      // Guest: never show a Download PDF control that cannot succeed — helper + auth upsell only.
      return (
        <>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-receipt-download-guest-helper"
          >
            {t('receipt.downloadDisabled.helper')}
          </p>
          {(() => {
            if (useInlineReceiptLogin) {
              return (
                <ReceiptLoginUpsellCard
                  onAuthenticated={handleReceiptAuthenticated}
                  onLoginStart={trackReceiptLoginCta}
                />
              );
            }
            if (!canMountSignIn) {
              return <SignedInAccountPromptCard />;
            }
            return (
              <Card padded>
                <p className="text-sm text-[var(--color-on-surface)]">{t('receipt.saveToAccount')}</p>
                <Button
                  intent="primary"
                  size="md"
                  block
                  className="mt-3"
                  data-testid="customer-receipt-login-cta"
                  onClick={handleLoginCta}
                >
                  {t('receipt.loginCta')}
                </Button>
              </Card>
            );
          })()}
        </>
      );
    }
    if (receiptRow !== null) {
      return (
        <AccountReceiptDownloadButton
          row={receiptRow}
          fullWidthOnNarrow
          screenName={screenName}
        />
      );
    }
    if (paymentsQuery.isLoading) {
      return <p className="text-sm text-[var(--color-on-surface-muted)]">{t('receipt.loading')}</p>;
    }
    return (
      <p className="text-sm text-[var(--color-on-surface-muted)]">{t('receipt.linkPending')}</p>
    );
  }

  return (
    <Card padded data-testid="checkout-receipt-panel">
      <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
        {t('receipt.title')}
      </h2>
      <dl className="mt-3 flex flex-col gap-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-[var(--color-on-surface-muted)]">{t('receipt.summary.paymentId')}</dt>
          <dd className="text-[var(--color-on-surface)]">{paymentStatus.paymentId}</dd>
        </div>
        {amountMinor !== undefined ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--color-on-surface-muted)]">{t('receipt.summary.amount')}</dt>
            <dd className="text-[var(--color-on-surface)]">
              {formatPrice(amountMinor / 100, currency)}
            </dd>
          </div>
        ) : null}
        {completedAt !== null && completedAt !== undefined ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--color-on-surface-muted)]">{t('receipt.summary.date')}</dt>
            <dd className="text-[var(--color-on-surface)]">{formatDateTime(completedAt)}</dd>
          </div>
        ) : null}
        {promoReceiptSnapshot != null && promoReceiptSnapshot.adjustments.length > 0 ? (
          <>
            {promoReceiptSnapshot.adjustments.map((adjustment) => (
              <div
                key={`${adjustment.lineKey}:${adjustment.eventId}`}
                className="flex items-center justify-between gap-3"
              >
                <dt className="text-[var(--color-on-surface-muted)]">
                  {t('promo.receiptLine', {
                    eventName: resolvePromoEventDisplayName({
                      eventId: adjustment.eventId,
                      storedEventName: adjustment.eventName,
                      fallbackLabel: t('promo.progressEventFallback')})})}
                </dt>
                <dd className="text-[var(--color-on-surface)]">
                  −{formatPrice(adjustment.discountAmount, currency)}
                </dd>
              </div>
            ))}
          </>
        ) : null}
      </dl>

      {!signedInReceipt && paymentStatus.receiptRecovery != null ? (
        <div className="mt-4">
          <GuestReceiptRecoveryBlock recovery={paymentStatus.receiptRecovery} />
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-3">{renderDownloadSection()}</div>
    </Card>
  );
}
